import { FetchHttpHandler } from '@smithy/fetch-http-handler'

// This bounds each HTTP attempt; the SDK retains its normal retry/backoff policy.
const publishingRequestTimeout = 15000

// Credential and Kinesis requests must release the publishing queue even when
// the network stalls. Abort the real fetch, including a stalled response body.
export class PublishingHttpHandler extends FetchHttpHandler {
  constructor(private readonly timeout = publishingRequestTimeout) {
    super()
  }

  async handle(...[request, options = {}]: Parameters<FetchHttpHandler['handle']>) {
    const controller = new AbortController()
    const upstreamSignal = options.abortSignal
    const previousAbort = upstreamSignal?.onabort
    const abort = function (this: NonNullable<typeof upstreamSignal>, event: unknown) {
      try {
        if (previousAbort) Reflect.apply(previousAbort, this, [event])
      } finally {
        controller.abort()
      }
    }
    if (upstreamSignal) {
      upstreamSignal.onabort = abort
      if (upstreamSignal.aborted) controller.abort()
    }
    const timer = setTimeout(() => controller.abort(), this.timeout)

    try {
      const result = await super.handle(request, { ...options, abortSignal: controller.signal })
      // The SDK fetch handler resolves at the headers. Keep cancellation active
      // until these small JSON responses have been read in full.
      if (result.response.body instanceof ReadableStream) {
        result.response.body = new Uint8Array(await new Response(result.response.body).arrayBuffer())
      }
      return result
    } finally {
      clearTimeout(timer)
      if (upstreamSignal?.onabort === abort) upstreamSignal.onabort = previousAbort || null
    }
  }
}
